import ApplicationController from "./application_controller"

// Misspelled search-result popup behavior:
// - hover indicator => open popup
// - keyboard: Enter/Space on indicator toggles popup; Escape closes
// - move indicator -> popup => keep open (with a small forgiveness delay for first-row "below" positioning)
// - leave popup => close
//
// This controller is mounted per misspelled wrapper element rendered by the helper.

export default class extends ApplicationController {
  static targets = ['misspelledIndicator', 'misspelledPopupCard']
  static classes = ['popupVisible', 'popupPositionBelow']
  static values = {
    // NOTE: This codebase uses an older Stimulus version that does not support
    // the object-style value definition ({ type, default }). Keep this in the
    // legacy Stimulus format for compatibility.
    firstRowCloseDelayMs: Number
  }

  connect() {
    this.closeTimeoutId = null
    this._tableResponsiveEl = null
    if (!this.hasMisspelledIndicatorTarget || !this.hasMisspelledPopupCardTarget) return

    this.updatePopupPosition()
    this.expandContainerForSingleRowPopup(this.element)

    this._onIndicatorEnter = () => this.handleIndicatorEnter()
    this._onIndicatorLeave = () => this.handleIndicatorLeave()
    this._onPopupEnter = () => this.handlePopupEnter()
    this._onPopupLeave = () => this.handlePopupLeave()
    this._onDocumentKeydown = (event) => {
      if (event.key !== 'Escape' || !this.isPopupOpen()) return
      event.preventDefault()
      this.closePopup(true)
    }

    this.misspelledIndicatorTarget.addEventListener('mouseenter', this._onIndicatorEnter)
    this.misspelledIndicatorTarget.addEventListener('mouseleave', this._onIndicatorLeave)
    this.misspelledPopupCardTarget.addEventListener('mouseenter', this._onPopupEnter)
    this.misspelledPopupCardTarget.addEventListener('mouseleave', this._onPopupLeave)
    document.addEventListener('keydown', this._onDocumentKeydown)
  }

  disconnect() {
    this.clearCloseTimeout()
    this.removeSingleRowRoomClass()

    if (this.hasMisspelledIndicatorTarget && this._onIndicatorEnter) {
      this.misspelledIndicatorTarget.removeEventListener('mouseenter', this._onIndicatorEnter)
    }
    if (this.hasMisspelledIndicatorTarget && this._onIndicatorLeave) {
      this.misspelledIndicatorTarget.removeEventListener('mouseleave', this._onIndicatorLeave)
    }
    if (this.hasMisspelledPopupCardTarget && this._onPopupEnter) {
      this.misspelledPopupCardTarget.removeEventListener('mouseenter', this._onPopupEnter)
    }
    if (this.hasMisspelledPopupCardTarget && this._onPopupLeave) {
      this.misspelledPopupCardTarget.removeEventListener('mouseleave', this._onPopupLeave)
    }
    if (this._onDocumentKeydown) {
      document.removeEventListener('keydown', this._onDocumentKeydown)
    }
  }

  // Misspelled indicator button hover
  handleIndicatorEnter() {
    this.clearCloseTimeout()
    this.openPopup()
  }

  handleIndicatorLeave() {
    if (this.isPopupPositionedBelow()) {
      this.scheduleClose()
    } else {
      this.closePopup()
    }
  }

  // Popup hover
  handlePopupEnter() {
    this.clearCloseTimeout()
    this.openPopup()
  }

  handlePopupLeave() {
    this.closePopup()
  }

  // Enter/Space toggles dialog; focus first popup action when opening (Escape handled in connect).
  handleIndicatorKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.stopPropagation()
    if (this.isPopupOpen()) {
      this.closePopup(true)
      return
    }
    this.openPopup()
    this.misspelledPopupCardTarget.querySelector('button')?.focus()
  }

  preventIndicatorWordSearch(event) {
    event.stopPropagation()
  }

  openContextModalFromPopup(event) {
    event.stopPropagation()
    event.preventDefault()

    const contextElement = this.resolvePopupContextElement(event)
    if (!contextElement?.dataset?.itemId) return

    const row = this.element.closest('tr')
    if (!row) return

    const contextCaller = this.application.getControllerForElementAndIdentifier(
      row,
      'image-context-view-caller'
    )
    if (!contextCaller) return

    // Delegate to the row's image-context-view-caller using the clicked image item's data-* ids.
    contextCaller.openImageModalAjaxCall({ currentTarget: contextElement, target: event.target })
  }

  // Image click: open that individual's context. Single-image popup: label/overlay click works too.
  // Multi-image (compound): each image opens its own context — no fallback to the first image.
  resolvePopupContextElement(event) {
    const imageItem = event.target.closest('.js-misspelled-popup-image-item')
    if (imageItem) return imageItem

    const imageItems = this.misspelledPopupCardTarget.querySelectorAll('.js-misspelled-popup-image-item')
    if (imageItems.length === 1) return imageItems[0]

    return null
  }

  // Internal helpers
  openPopup() {
    this.setPopupOpen(true)
  }

  closePopup(restoreIndicatorFocus = false) {
    this.setPopupOpen(false, restoreIndicatorFocus)
  }

  // Keeps popupVisibleClass, aria-expanded, and aria-hidden in sync for the dialog pattern.
  setPopupOpen(open, restoreIndicatorFocus = false) {
    this.misspelledPopupCardTarget.classList.toggle(this.popupVisibleClass, open)
    this.misspelledIndicatorTarget.setAttribute('aria-expanded', String(open))
    this.misspelledPopupCardTarget.setAttribute('aria-hidden', String(!open))
    if (!open && restoreIndicatorFocus) this.misspelledIndicatorTarget.focus()
  }

  isPopupOpen() {
    return this.hasMisspelledPopupCardTarget &&
      this.misspelledPopupCardTarget.classList.contains(this.popupVisibleClass)
  }

  scheduleClose() {
    this.clearCloseTimeout()
    this.closeTimeoutId = setTimeout(() => {
      this.closePopup()
      this.closeTimeoutId = null
    }, this.firstRowCloseDelayMs())
  }

  clearCloseTimeout() {
    if (!this.closeTimeoutId) return
    clearTimeout(this.closeTimeoutId)
    this.closeTimeoutId = null
  }

  firstRowCloseDelayMs() {
    if (this.hasFirstRowCloseDelayMsValue) return this.firstRowCloseDelayMsValue
    return 175
  }

  detectFirstRow(wrapperElement) {
    const tableRow = wrapperElement.closest('tr')
    if (!tableRow) return false

    const tableBody = tableRow.parentElement
    if (!tableBody || tableBody.tagName !== 'TBODY') return false

    return tableBody.firstElementChild === tableRow
  }

  // First row: popup below. All other rows: popup above.
  updatePopupPosition() {
    if (this.detectFirstRow(this.element)) {
      this.misspelledPopupCardTarget.classList.add(this.popupPositionBelowClass)
    } else {
      this.misspelledPopupCardTarget.classList.remove(this.popupPositionBelowClass)
    }
  }

  // Single result row: makes the scroll container overflow-visible so the popup escapes it (no clipping, no scrollbar).
  expandContainerForSingleRowPopup(wrapperElement) {
    const tableRow = wrapperElement.closest('tr')
    if (!tableRow) return

    const tableBody = tableRow.parentElement
    if (!tableBody || tableBody.tagName !== 'TBODY') return

    const rowCount = tableBody.children.length
    if (rowCount !== 1) return

    this._tableResponsiveEl = wrapperElement.closest('.table-responsive')
    if (!this._tableResponsiveEl) return
    this._tableResponsiveEl.classList.add('misspelled-single-row-room')
    this.updateSingleRowPopupVerticalRoom()
  }

  updateSingleRowPopupVerticalRoom() {
    if (!this._tableResponsiveEl || !this.hasMisspelledPopupCardTarget) return

    const imageCount = this.misspelledPopupCardTarget.querySelectorAll('.js-misspelled-popup-image-item').length
    const requiredVerticalSpace = this.misspelledPopupVerticalRoomPx(imageCount)
    this._tableResponsiveEl.style.setProperty('--misspelled-popup-vertical-room', `${requiredVerticalSpace}px`)
  }

  // Multi-image popups use a single scroll row (3 tiles visible); height is constant regardless of count.
  misspelledPopupVerticalRoomPx(imageCount) {
    if (imageCount <= 1) return 75

    const imageTileHeightPx = 72
    const popupChromeHeightPx = 48

    return popupChromeHeightPx + imageTileHeightPx
  }

  removeSingleRowRoomClass() {
    if (!this._tableResponsiveEl) return
    this._tableResponsiveEl.classList.remove('misspelled-single-row-room')
    this._tableResponsiveEl.style.removeProperty('--misspelled-popup-vertical-room')
    this._tableResponsiveEl = null
  }

  isPopupPositionedBelow() {
    return this.misspelledPopupCardTarget.classList.contains(this.popupPositionBelowClass)
  }
}
